// File: frontend/src/pages/ReviewPage.jsx

/*
  ReviewPage allows the Ministry Lead to:
  - Select a date to load pending report data
  - Review and optionally edit each field's value
  - Submit the finalized report to the backend
  Displays visual indicators for missing or edited fields.
*/

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { get, post, getErrorMessage } from '../api/client';
import { fieldGroups, fieldLabels, reportFormFieldKeys } from '../fieldConfig';

// Format date for display (e.g., "10 Feb 2026")
const formatDateReadable = (isoDate) => {
  if (!isoDate) return '';
  const d = new Date(isoDate + 'T12:00:00');
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const ReviewPage = () => {
  // Selected date to review
  const [selectedDate, setSelectedDate] = useState('');

  // Final report object and field statuses
  const [finalReport, setFinalReport] = useState({});
  const [status, setStatus] = useState({});

  // Error, info, or success message display
  const [errorMessage, setErrorMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  // Success state: show confirmation view instead of form
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submittedDate, setSubmittedDate] = useState('');
  const [submittedReport, setSubmittedReport] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savingFieldKey, setSavingFieldKey] = useState(null); // which field is being saved
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('');

  // Section comments from submitters (group name -> comment)
  const [sectionComments, setSectionComments] = useState({});

  // Confirmation modal before submitting final report
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  // Fetch pending report data for selected date
  useEffect(() => {
    if (!selectedDate) return;

    const fetchPendingData = async () => {
      try {
        const { ok, status, data } = await get(`/api/pending/${selectedDate}`);

        // 404 = no pending entries for this date
        if (status === 404) {
          setFinalReport({});
          setStatus({});
          setSectionComments({});
          setInfoMessage('No pending data for this date yet. Enter values below and save them, or have team members submit via "Add Group Data."');
          setErrorMessage('');
          return;
        }

        if (!ok) {
          setErrorMessage(getErrorMessage({ status, data }, 'Failed to load pending data.'));
          setInfoMessage('');
          return;
        }

        const grouped = {};
        const fieldStatus = {};
        const commentsBySection = {};

        (Array.isArray(data) ? data : []).forEach(entry => {
          if (entry.type === 'ministryLeadComment') {
            grouped.comment = entry.comment || '';
            fieldStatus.comment = entry.comment ? 'edited' : 'missing';
          } else {
            grouped[entry.type] = entry.value;
            fieldStatus[entry.type] = entry.value !== undefined ? 'edited' : 'missing';
            // Derive group from field if not in payload (legacy data)
            const group = entry.group || Object.entries(fieldGroups).find(([, fields]) => fields.includes(entry.type))?.[0] || '';
            if (group && entry.comment) {
              commentsBySection[group] = entry.comment;
            }
          }
        });

        setFinalReport(grouped);
        setStatus(fieldStatus);
        setSectionComments(commentsBySection);
        setErrorMessage('');
        setInfoMessage('');
      } catch (err) {
        console.error('Error fetching pending data:', err);
        setFinalReport({});
        setStatus({});
        setSectionComments({});
        setErrorMessage('Failed to load pending data. Is the backend running?');
        setInfoMessage('');
      }
    };

    fetchPendingData();
  }, [selectedDate]);

  // Update final report values and mark fields as edited
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFinalReport(prev => ({ ...prev, [name]: value }));
    setStatus(prev => ({ ...prev, [name]: 'edited' }));
  };

  // Show confirmation modal when user clicks Submit Final Report
  const handleSubmitClick = (e) => {
    e.preventDefault();
    setShowSubmitConfirm(true);
  };

  // Submit finalized report to backend (called after user confirms)
  const handleConfirmSubmit = async () => {
    setShowSubmitConfirm(false);
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const { ok, status, data } = await post('/api/reports', { ...finalReport, date: selectedDate });

      if (status === 409) {
        setErrorMessage(getErrorMessage({ status, data }, 'Duplicate submission not allowed.'));
        setInfoMessage('');
        setIsSubmitting(false);
        return;
      }

      if (!ok) {
        setErrorMessage(getErrorMessage({ status, data }, 'An unexpected error occurred.'));
        setInfoMessage('');
        setIsSubmitting(false);
        return;
      }

      setSubmittedDate(selectedDate);
      setSubmittedReport({ ...finalReport });
      setSubmitSuccess(true);
      setErrorMessage('');
      setInfoMessage('');
      setFinalReport({});
      setSectionComments({});
      setSelectedDate('');
      setStatus({});
    } catch (err) {
      console.error('Submission error:', err);
      setErrorMessage('An unexpected error occurred.');
      setInfoMessage('');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Save a single field to pending (temporary)
  const handleSaveField = async (fieldKey, sectionName) => {
    if (!selectedDate) return;
    const val = fieldKey === 'comment' ? (finalReport.comment || '').trim() : finalReport[fieldKey];
    if (fieldKey === 'comment') {
      if (!val) return;
    } else if (val === undefined || val === '' || val === null) return;

    setSavingFieldKey(fieldKey);
    setErrorMessage('');
    setSaveSuccessMessage('');
    try {
      const payload = fieldKey === 'comment'
        ? {
            date: selectedDate,
            type: 'ministryLeadComment',
            value: 0,
            comment: val,
            group: '',
            replace: true,
          }
        : {
            date: selectedDate,
            type: fieldKey,
            value: Number(val) || 0,
            group: sectionName,
            replace: true,
          };
      const { ok, status, data } = await post('/api/pending', payload);
      if (!ok) throw new Error(getErrorMessage({ status, data }, `Failed to save ${fieldKey}`));
      setStatus(prev => ({ ...prev, [fieldKey]: 'edited' }));
      setSaveSuccessMessage('Saved.');
      setTimeout(() => setSaveSuccessMessage(''), 2000);
    } catch (err) {
      console.error('Save field error:', err);
      setErrorMessage(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSavingFieldKey(null);
    }
  };

  // Save current values to pending (temporary) so leader can add more later or submit when ready
  const handleSaveToPending = async (e) => {
    e.preventDefault();
    if (!selectedDate) {
      setErrorMessage('Please select a date first.');
      setInfoMessage('');
      setSaveSuccessMessage('');
      return;
    }

    const numericKeys = reportFormFieldKeys || [];
    const hasNumericValues = numericKeys.some(k => finalReport[k] !== undefined && finalReport[k] !== '' && finalReport[k] !== null);
    const hasComment = (finalReport.comment || '').trim() !== '';
    if (!hasNumericValues && !hasComment) {
      setErrorMessage('Enter at least one field or note before saving.');
      setInfoMessage('');
      setSaveSuccessMessage('');
      return;
    }

    setIsSaving(true);
    setErrorMessage('');
    setSaveSuccessMessage('');
    setInfoMessage('');

    try {
      for (const [sectionName, fieldKeys] of Object.entries(fieldGroups)) {
        for (const field of fieldKeys) {
          const val = finalReport[field];
          if (val !== undefined && val !== '' && val !== null) {
            const payload = {
              date: selectedDate,
              type: field,
              value: Number(val) || 0,
              group: sectionName,
              replace: true,
            };
            const { ok, status, data } = await post('/api/pending', payload);
            if (!ok) throw new Error(getErrorMessage({ status, data }, `Failed to save ${field}`));
          }
        }
      }

      if (hasComment) {
        const { ok, status, data } = await post('/api/pending', {
          date: selectedDate,
          type: 'ministryLeadComment',
          value: 0,
          comment: (finalReport.comment || '').trim(),
          group: '',
          replace: true,
        });
        if (!ok) throw new Error(getErrorMessage({ status, data }, 'Failed to save Ministry Lead Notes'));
      }

      setSaveSuccessMessage('Saved to pending. You can add more fields or submit when ready.');
      setErrorMessage('');
    } catch (err) {
      console.error('Save to pending error:', err);
      setErrorMessage(err?.message || 'Failed to save. Please try again.');
      setSaveSuccessMessage('');
    } finally {
      setIsSaving(false);
    }
  };

  // Success confirmation view (similar to ThankYouPage for Add Group Data)
  if (submitSuccess) {
    return (
      <div className="entry-page">
        <div className="entry-success-card">
          <div className="success-icon">✓</div>
          <h2>Report Submitted!</h2>
          <p style={{ color: 'var(--cr-text-muted)', margin: '0 0 0.5rem 0' }}>You've successfully submitted the final report for:</p>
          <p style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--cr-text)', margin: '0 0 1.5rem 0' }}>
            {formatDateReadable(submittedDate)}
          </p>
          <div className="success-summary">
            <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.8rem', color: 'var(--cr-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Submitted data summary</p>
            {(reportFormFieldKeys || Object.keys(submittedReport))
              .filter(key => key !== 'comment' && submittedReport[key] !== undefined && submittedReport[key] !== '' && submittedReport[key] !== null)
              .map(key => (
                <div key={key} style={{ marginBottom: '0.35rem', fontSize: '0.95rem', color: 'var(--cr-text)' }}>
                  <strong>{fieldLabels[key] ?? key}:</strong> {submittedReport[key]}
                </div>
              ))}
            {submittedReport.comment && (
              <div style={{ marginTop: '0.5rem', fontSize: '0.95rem', fontStyle: 'italic', color: 'var(--cr-text-muted)' }}>
                <strong>Note:</strong> {submittedReport.comment}
              </div>
            )}
          </div>
          <div className="success-buttons">
            <button type="button" onClick={() => { setSubmitSuccess(false); setSubmittedDate(''); setSubmittedReport({}); }} className="btn-primary">Ministry Lead: Review Another</button>
            <Link to="/gatherings" className="btn-outline">Back to Gatherings</Link>
          </div>
        </div>
      </div>
    );
  }

  // Render instruction box, date picker, editable inputs, and submit button
  return (
    <div style={{ maxWidth: '700px', width: '100%', margin: '0 auto', padding: '0 1rem 6rem 1rem', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <PageHeader title="Ministry Lead Review" section="gatherings" />
      <div style={{ backgroundColor: 'var(--cr-bg-warm)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid var(--cr-border)' }}>
        <button
          type="button"
          id="ministry-review-instructions-toggle"
          aria-expanded={instructionsOpen}
          aria-controls="ministry-review-instructions"
          onClick={() => setInstructionsOpen((o) => !o)}
          style={{
            width: '100%',
            textAlign: 'left',
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            font: 'inherit',
            color: 'var(--cr-text)',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '0.35rem 0.5rem',
          }}
        >
          <span aria-hidden>{instructionsOpen ? '▼' : '▶'}</span>
          <strong>Instructions</strong>
          <span style={{ fontSize: '0.9rem', color: 'var(--cr-text-muted)', fontWeight: 400 }}>
            {instructionsOpen ? '(hide)' : '(tap to expand)'}
          </span>
        </button>
        {instructionsOpen && (
          <ol id="ministry-review-instructions" style={{ paddingLeft: '1.2rem', margin: '0.75rem 0 0 0' }}>
            <li><strong>Select a date.</strong> Choose the Friday night date for the report you want to review. Team members may have submitted data via &quot;Add Group Data,&quot; or you can enter values yourself.</li>
            <li><strong>Review and edit the numbers.</strong> The fields below show what your team entered (if any). Check each section, correct any errors, and fill in any missing values. Fields marked &quot;Missing&quot; need your input before you can submit.</li>
            <li><strong>Save temporarily.</strong> If you have only some fields, click &quot;Save to Pending&quot; to store them. You can return later to add more or submit the final report.</li>
            <li><strong>Submit the final report.</strong> When everything looks correct, click &quot;Submit Final Report&quot; to save and finalize. This clears pending entries and sends the summary email.</li>
          </ol>
        )}
      </div>

      {errorMessage && (
        <div style={{ backgroundColor: 'var(--cr-bg-warm)', color: 'var(--cr-text)', padding: '0.75rem', marginBottom: '1rem', borderRadius: '8px', border: '1px solid var(--cr-border)' }}>
          {errorMessage}
        </div>
      )}
      {infoMessage && (
        <div style={{ backgroundColor: 'var(--cr-bg-warm)', color: 'var(--cr-text-muted)', padding: '0.75rem', marginBottom: '1rem', borderRadius: '8px', border: '1px solid var(--cr-border)' }}>
          {infoMessage}
        </div>
      )}
      {saveSuccessMessage && (
        <div style={{ backgroundColor: 'var(--cr-success-bg)', color: 'var(--cr-success)', padding: '0.75rem', marginBottom: '1rem', borderRadius: '8px', border: '1px solid var(--cr-success-border)' }}>
          {saveSuccessMessage}
        </div>
      )}

      <form onSubmit={handleSubmitClick}>
        <div style={{ marginBottom: '1rem' }}>
          <label htmlFor="review-report-date"><strong>Select Date:</strong></label>
          <div
            style={{
              position: 'relative',
              width: '100%',
              marginTop: '0.25rem',
            }}
          >
            <div
              aria-hidden="true"
              style={{
                padding: '0.35rem 0.5rem',
                border: '1px solid var(--cr-border)',
                borderRadius: '8px',
                backgroundColor: 'var(--cr-surface)',
                color: selectedDate ? 'var(--cr-text)' : 'var(--cr-text-muted)',
                fontSize: '1rem',
              }}
            >
              {selectedDate ? formatDateReadable(selectedDate) : 'Tap to select date'}
            </div>
            <input
              id="review-report-date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              onClick={(e) => e.target.showPicker?.()}
              required
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                opacity: 0,
                cursor: 'pointer',
                margin: 0,
                zIndex: 1,
              }}
            />
          </div>
        </div>

        {Object.entries(fieldGroups).map(([sectionName, fieldKeys]) => (
          <div key={sectionName} style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--cr-bg-warm)', borderRadius: '8px', border: '1px solid var(--cr-border)' }}>
            <h2 style={{ margin: '0 0 0.5rem 0', color: 'var(--cr-text)', fontSize: '1.1rem' }}>{sectionName}</h2>
            {sectionComments[sectionName] && (
              <div style={{ marginBottom: '0.75rem', padding: '0.5rem', backgroundColor: 'var(--cr-surface)', borderRadius: '6px', fontSize: '0.95rem', color: 'var(--cr-text)', border: '1px solid var(--cr-border)' }}>
                <strong>Comments from submitter:</strong> {sectionComments[sectionName]}
              </div>
            )}
            {fieldKeys.map((key) => {
              const hasValue = finalReport[key] !== undefined && finalReport[key] !== '' && finalReport[key] !== null;
              const isSavingThis = savingFieldKey === key;
              const fieldId = `review-field-${sectionName.replace(/[^a-zA-Z0-9_-]/g, '-')}-${key}`;
              return (
              <div key={key} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem' }}>
                <label htmlFor={fieldId} style={{ flex: 1, color: 'var(--cr-text)' }}>{fieldLabels[key]}:</label>
                <input
                  id={fieldId}
                  type="number"
                  name={key}
                  value={finalReport[key] ?? ''}
                  onChange={handleChange}
                  style={{ flex: 2, textAlign: 'center' }}
                />
                {hasValue && (
                  <button
                    type="button"
                    onClick={() => handleSaveField(key, sectionName)}
                    disabled={!selectedDate || isSavingThis || isSaving || isSubmitting}
                    style={{
                      padding: '0.35rem 0.6rem',
                      fontSize: '0.85rem',
                      backgroundColor: !selectedDate || isSavingThis || isSaving || isSubmitting ? 'var(--cr-text-muted)' : 'var(--cr-button-secondary)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: !selectedDate || isSavingThis || isSaving || isSubmitting ? 'not-allowed' : 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isSavingThis ? '…' : 'Save'}
                  </button>
                )}
                <span style={{ flex: 1, color: status[key] === 'edited' ? 'var(--cr-text-muted)' : 'var(--cr-text)', fontSize: '0.9rem' }}>
                  {status[key] === 'edited' ? '✓ edited' : 'Missing'}
                </span>
              </div>
              );
            })}
          </div>
        ))}

        <div style={{ marginBottom: '0.5rem', padding: '1rem', backgroundColor: 'var(--cr-bg-warm)', borderRadius: '8px', border: '1px solid var(--cr-border)' }}>
          <h2 style={{ margin: '0 0 0.5rem 0', color: 'var(--cr-text)', fontSize: '1.1rem' }}>Ministry Lead Notes</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label htmlFor="review-field-comment" style={{ flex: 1, color: 'var(--cr-text)' }}>{fieldLabels.comment}:</label>
            <input
              id="review-field-comment"
              type="text"
              name="comment"
              value={finalReport.comment || ''}
              onChange={handleChange}
              placeholder="Overall notes or comments for the report"
              style={{ flex: 2, textAlign: 'left' }}
            />
            {(finalReport.comment || '').trim() !== '' && (
              <button
                type="button"
                onClick={() => handleSaveField('comment', '')}
                disabled={!selectedDate || savingFieldKey === 'comment' || isSaving || isSubmitting}
                style={{
                  padding: '0.35rem 0.6rem',
                  fontSize: '0.85rem',
                  backgroundColor: !selectedDate || savingFieldKey === 'comment' || isSaving || isSubmitting ? 'var(--cr-text-muted)' : 'var(--cr-button-secondary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: !selectedDate || savingFieldKey === 'comment' || isSaving || isSubmitting ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {savingFieldKey === 'comment' ? '…' : 'Save'}
              </button>
            )}
            <span style={{ flex: 1, color: 'var(--cr-text-muted)', fontSize: '0.9rem' }}>Optional</span>
          </div>
        </div>

        {(() => {
          const numericKeys = reportFormFieldKeys || [];
          const hasAnyValues = numericKeys.some(k => finalReport[k] !== undefined && finalReport[k] !== '' && finalReport[k] !== null) || (finalReport.comment || '').trim() !== '';
          return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center', marginTop: '1rem' }}>
          <button
            type="button"
            onClick={handleSaveToPending}
            disabled={!selectedDate || !hasAnyValues || isSaving || isSubmitting}
            style={{
              padding: '0.65rem 1.25rem',
              backgroundColor: !selectedDate || !hasAnyValues || isSaving || isSubmitting ? 'var(--cr-text-muted)' : 'var(--cr-button-secondary)',
              color: 'var(--white)',
              border: '1px solid var(--cr-border)',
              borderRadius: '8px',
              cursor: !selectedDate || !hasAnyValues || isSaving || isSubmitting ? 'not-allowed' : 'pointer',
              fontWeight: 500
            }}
          >
            {isSaving ? 'Saving…' : 'Save to Pending'}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            style={{
              padding: '0.65rem 1.25rem',
              backgroundColor: isSubmitting ? 'var(--cr-text-muted)' : 'var(--cr-button)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              fontWeight: 500
            }}
          >
            {isSubmitting ? 'Submitting…' : 'Submit Final Report'}
          </button>
        </div>
          );
        })()}
      </form>

      {showSubmitConfirm && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem',
          }}
          onClick={() => setShowSubmitConfirm(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--cr-surface)',
              borderRadius: '12px',
              padding: '1.5rem',
              maxWidth: '400px',
              width: '100%',
              border: '1px solid var(--cr-border)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem 0', color: 'var(--cr-text)', fontSize: '1.2rem' }}>Are you sure?</h3>
            <p style={{ margin: '0 0 1rem 0', color: 'var(--cr-text-muted)', fontSize: '0.95rem' }}>
              Please review the numbers below before submitting the final report for {formatDateReadable(selectedDate)}:
            </p>
            <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'var(--cr-bg-warm)', borderRadius: '8px', border: '1px solid var(--cr-border)' }}>
              {(reportFormFieldKeys || [])
                .filter(key => finalReport[key] !== undefined && finalReport[key] !== '' && finalReport[key] !== null)
                .map(key => (
                  <div key={key} style={{ marginBottom: '0.35rem', fontSize: '0.95rem', color: 'var(--cr-text)' }}>
                    <strong>{fieldLabels[key] ?? key}:</strong> {finalReport[key]}
                  </div>
                ))}
              {finalReport.comment && (
                <div style={{ marginTop: '0.5rem', fontSize: '0.95rem', fontStyle: 'italic', color: 'var(--cr-text-muted)' }}>
                  <strong>Note:</strong> {finalReport.comment}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowSubmitConfirm(false)}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'transparent',
                  color: 'var(--cr-text)',
                  border: '1px solid var(--cr-border)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: 'var(--cr-button)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 500,
                }}
              >
                Yes, submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewPage;
